// OnboardingTooltipCard.tsx
import { ArrowRight } from 'lucide-react';
import React, { useContext } from 'react';
import type { TooltipRenderProps } from 'react-joyride';

import { MyHiveLocalizationKey } from '../localization/my-hive.js';
import { myhiveLocalization } from '../localization/my-hive.js';
import { LanguageContext } from '../providers/LanguageProvider.js';
import type { Flow } from './lib/steps.js';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

type Props = TooltipRenderProps & {
  stepLabel?: string;

  // Optional custom orange button (independent behavior)
  orangeButton?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
  };
  flow?: Flow;
  isLastStep?: boolean;
  handleContinue?: () => void;
  buttonText?: string;
};

export default function OnboardingTooltipCard(props: Props): React.JSX.Element {
  const { step, primaryProps, tooltipProps, orangeButton, flow, isLastStep, handleContinue, buttonText } = props;

  const nextBtnProps = primaryProps as ButtonProps;
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  const stepLabelMap: Record<string, string> = {
    GET_FAMILIAR: t[MyHiveLocalizationKey.GetFamiliar],
    GROW: t[MyHiveLocalizationKey.LetsGrow],
    PERSONALIZE: t[MyHiveLocalizationKey.MakeItYourOwn],
    MAXIMIZE: t[MyHiveLocalizationKey.MaximizeImpact],
  };
  const resolvedStepLabel = stepLabelMap[flow ?? ''] ?? '';

  const title = resolveText(step.title, t);
  const body = resolveText(step.content, t);
  const orangeButtonLabel = resolveText(orangeButton?.label, t);

  return (
    <div {...tooltipProps}>
      <div className="font-inter relative max-w-md rounded-2xl bg-white px-8 py-7 shadow-sm ring-1 ring-black/5">
        {/* Step label */}
        <div className="text-sm font-medium text-black/70">{resolvedStepLabel}</div>

        {/* Title */}
        <h2 className="mt-3 text-xl font-semibold leading-[1.1] tracking-[-0.02em] text-black">{title}</h2>

        {/* Body */}
        <p className="mt-3 whitespace-pre-line text-black/50">{body}</p>

        <div className="mt-8 flex flex-wrap items-center justify-end gap-4">
          {/* Optional orange custom button */}
          {orangeButton && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                orangeButton.onClick();
              }}
              disabled={orangeButton.disabled}
              className={[
                `
                inline-flex items-center gap-2 rounded-full
                bg-alveole-orange-500 px-5 py-2.5 text-sm text-black
                shadow-sm hover:brightness-105 hover:cursor-pointer
                disabled:opacity-50 disabled:cursor-not-allowed
                transition
              `,
                orangeButton.className ?? '',
              ].join(' ')}
            >
              {orangeButtonLabel}
              <ArrowRight className="h-5 w-5" />
            </button>
          )}

          {/* Dark next button */}
          <button
            type="button"
            {...nextBtnProps}
            className={[
              `
                inline-flex items-center gap-2 rounded-full
                bg-alveole-900 px-5 py-2.5 text-sm text-white
                shadow-sm hover:bg-alveole-800 hover:cursor-pointer
                disabled:opacity-50 disabled:cursor-not-allowed
                transition
              `,
              nextBtnProps.className ?? '',
            ].join(' ')}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleContinue?.();
            }}
          >
            {buttonText ?? (isLastStep ? t[MyHiveLocalizationKey.Finish] : t[MyHiveLocalizationKey.Next])}
            {isLastStep ? ' ' : <ArrowRight className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </div>
  );

  function resolveText(value: unknown, t: Record<string, string>): string {
    if (typeof value === 'string') {
      // If it's a localization key, it will also be a string at runtime,
      // so we check if it exists in the dictionary.
      return t[value as MyHiveLocalizationKey] ?? value;
    }
    return '';
  }
}
